// src/components/Organizer/TicketManagement.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../utils/api";
import { toast } from "react-toastify";
import { formatJapaneseDateTime } from "../../utils/dateUtils";

// Define types for our data
type Event = {
	id: number;
	external_id: string;
	title: string;
	date: string;
	time: string;
	venue_name: string;
};

type OrganizerEvent = Event & {
	status?: "draft" | "published";
	prefecture?: string;
	city?: string;
	image_url?: string;
	totalTickets?: number;
	soldTickets?: number;
};

type TicketType = {
	id: number;
	name: string;
	price: number;
	is_free: boolean;
	has_limit: boolean;
	quantity: number | null;
	sold_tickets: number;
};

type Ticket = {
	id: number;
	ticket_code: string;
	status: string;
	is_redeemed: boolean;
	redeemed_at: string | null;
	sequence_number: number;
};

type Purchaser = {
	id: number;
	order_id: number;
	ticket_type_id: number;
	user_name: string;
	email: string;
	ticket_type: string;
	quantity: number;
	purchase_date: string;
	status: string;
	tickets: Ticket[];
};

type EventFilter = "all" | "published" | "draft" | "past";

const TicketManagement: React.FC = () => {
	const { id: eventExternalIdParam } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const eventExternalId = eventExternalIdParam ?? null;
	const [isLoading, setIsLoading] = useState(true);
	const [event, setEvent] = useState<Event | null>(null);
	const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
	const [purchasers, setPurchasers] = useState<Purchaser[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [events, setEvents] = useState<OrganizerEvent[]>([]);
	const [isEventsLoading, setIsEventsLoading] = useState(!eventExternalId);
	const [eventsError, setEventsError] = useState<string | null>(null);
	const [eventFilter, setEventFilter] = useState<EventFilter>("all");
	const [selectedTicketType, setSelectedTicketType] = useState<string>("all");

	useEffect(() => {
		const fetchEventData = async () => {
			if (!eventExternalId) {
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				// イベント情報を取得
				const eventResponse = await apiClient.get(
					`/organizer/events/${eventExternalId}`
				);
				setEvent({
					id: eventResponse.data.event.id,
					external_id: eventResponse.data.event.external_id,
					title: eventResponse.data.event.title,
					date: eventResponse.data.event.date,
					time: eventResponse.data.event.time,
					venue_name:
						eventResponse.data.event.venue_name ||
						`${eventResponse.data.event.prefecture} ${eventResponse.data.event.city}`,
				});

				// チケットタイプ情報を取得
				setTicketTypes(eventResponse.data.event.ticket_types);

				// 購入者情報を取得
				try {
					const purchasersResponse = await apiClient.get(
						`/organizer/events/${eventExternalId}/purchases`
					);
					setPurchasers(purchasersResponse.data.purchasers || []);
				} catch (purchaseError) {
					console.warn("購入者情報の取得に失敗しました:", purchaseError);
					setPurchasers([]);
				}

				setIsLoading(false);
			} catch (error) {
				console.error("イベントデータの取得に失敗しました:", error);
				setError("イベントデータの取得に失敗しました");
				setIsLoading(false);
				toast.error("イベントデータの取得に失敗しました");
			}
		};

		fetchEventData();
	}, [eventExternalId]);

	const fetchOrganizerEvents = useCallback(async () => {
		if (eventExternalId) return;

		setIsEventsLoading(true);
		setEventsError(null);

		try {
			const queryParams = new URLSearchParams();
			if (eventFilter === "published" || eventFilter === "draft") {
				queryParams.append("status", eventFilter);
			}
			if (eventFilter === "past") {
				queryParams.append("timeframe", "past");
			}

			const queryString = queryParams.toString();
			const endpoint = queryString
				? `/organizer/events?${queryString}`
				: "/organizer/events";

			const response = await apiClient.get(endpoint);
			setEvents(response.data.events || []);
		} catch (err) {
			console.error("イベント一覧の取得に失敗しました:", err);
			setEventsError("イベント一覧の取得に失敗しました");
			toast.error("イベント一覧の取得に失敗しました");
		} finally {
			setIsEventsLoading(false);
		}
	}, [eventFilter, eventExternalId]);

	useEffect(() => {
		fetchOrganizerEvents();
	}, [fetchOrganizerEvents]);

	const handleEventSelect = (selectedExternalId: string) => {
		navigate(`/organizer/events/${selectedExternalId}/tickets`);
	};

	const handleToggleCheckIn = async (ticketId: number, isRedeemed: boolean) => {
		try {
			const action = isRedeemed ? "undo_check_in" : "check_in";
			await apiClient.post("/organizer/check-in/toggle", {
				ticket_id: ticketId,
				action: action,
			});

			toast.success(
				isRedeemed ? "チェックインを取り消しました" : "チェックインしました"
			);

			// 購入者一覧を再取得
			if (eventExternalId) {
				const purchasersResponse = await apiClient.get(
					`/organizer/events/${eventExternalId}/purchases`
				);
				setPurchasers(purchasersResponse.data.purchasers || []);
			}
		} catch (error) {
			console.error("チェックイン状態の切り替えに失敗しました:", error);
			toast.error("チェックイン状態の切り替えに失敗しました");
		}
	};

	const handleExportCSV = () => {
		// CSVエクスポート機能の実装（将来的に実装）
		toast.info("CSVダウンロード機能は準備中です");
	};

	// 購入者をemailでグルーピングし、フィルタリング
	const getGroupedAndFilteredPurchasers = () => {
		// まずチケット種別でフィルタリング
		let filtered = purchasers;
		if (selectedTicketType !== "all") {
			filtered = purchasers.filter(
				(p) => p.ticket_type_id.toString() === selectedTicketType
			);
		}

		// emailでグルーピング
		const grouped = new Map<
			string,
			{
				user_name: string;
				email: string;
				ticketsByType: Map<
					number,
					{
						ticket_type: string;
						tickets: Ticket[];
					}
				>;
				totalQuantity: number;
				totalCheckedIn: number;
			}
		>();

		filtered.forEach((purchaser) => {
			const key = purchaser.email;
			if (!grouped.has(key)) {
				grouped.set(key, {
					user_name: purchaser.user_name,
					email: purchaser.email,
					ticketsByType: new Map(),
					totalQuantity: 0,
					totalCheckedIn: 0,
				});
			}
			const group = grouped.get(key)!;

			// チケット種別ごとにまとめる
			if (!group.ticketsByType.has(purchaser.ticket_type_id)) {
				group.ticketsByType.set(purchaser.ticket_type_id, {
					ticket_type: purchaser.ticket_type,
					tickets: [],
				});
			}
			const typeGroup = group.ticketsByType.get(purchaser.ticket_type_id)!;
			typeGroup.tickets.push(...purchaser.tickets);

			group.totalQuantity += purchaser.quantity;
			group.totalCheckedIn += purchaser.tickets.filter(
				(t) => t.is_redeemed
			).length;
		});

		return Array.from(grouped.values());
	};

	const renderEventSelectionContent = () => {
		if (eventsError) {
			return (
				<div className="text-center py-10 bg-red-50 dark:bg-red-900/20 rounded-lg">
					<p className="text-red-600 dark:text-red-300">{eventsError}</p>
					<button
						onClick={fetchOrganizerEvents}
						className="mt-4 inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
					>
						再読み込み
					</button>
				</div>
			);
		}

		if (events.length === 0 && !isEventsLoading) {
			return (
				<div className="text-center py-10 bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
					<p className="text-gray-600 dark:text-gray-400">
						イベントがまだ登録されていません
					</p>
					<button
						onClick={() => navigate("/organizer/events/create")}
						className="text-violet-600 dark:text-violet-400 hover:underline mt-2"
					>
						イベントを作成する
					</button>
				</div>
			);
		}

		if (events.length === 0) {
			return (
				<div className="text-center py-10 text-gray-600 dark:text-gray-400">
					イベントを読み込んでいます...
				</div>
			);
		}

		return (
			<div className="space-y-4">
				{events.map((organizerEvent) => (
					<button
						key={organizerEvent.external_id}
						onClick={() => handleEventSelect(organizerEvent.external_id)}
						className="w-full text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-sm transition"
					>
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
							<div>
								<h3 className="font-semibold text-lg text-gray-900 dark:text-white">
									{organizerEvent.title}
								</h3>
								<p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
									📅{" "}
									{formatJapaneseDateTime(
										organizerEvent.date,
										organizerEvent.time
									)}
								</p>
								<p className="text-gray-600 dark:text-gray-400 text-sm">
									📍
									{organizerEvent.venue_name ||
										`${organizerEvent.prefecture ?? ""} ${
											organizerEvent.city ?? ""
										}`.trim()}
								</p>
							</div>
							<div className="text-sm text-gray-600 dark:text-gray-400 flex flex-col items-start md:items-end">
								{typeof organizerEvent.totalTickets === "number" && (
									<span>
										販売状況: {organizerEvent.soldTickets ?? 0}/
										{organizerEvent.totalTickets} 枚
									</span>
								)}
								{organizerEvent.status && (
									<span
										className={`mt-2 px-2 py-1 rounded-full text-xs ${
											organizerEvent.status === "published"
												? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
												: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
										}`}
									>
										{organizerEvent.status === "published"
											? "公開中"
											: "下書き"}
									</span>
								)}
							</div>
						</div>
					</button>
				))}
			</div>
		);
	};

	const eventFilterOptions: { value: EventFilter; label: string }[] = [
		{ value: "all", label: "すべて" },
		{ value: "published", label: "公開中" },
		{ value: "draft", label: "下書き" },
		{ value: "past", label: "過去のイベント" },
	];

	// イベントIDがない場合、イベント選択画面を表示
	if (!eventExternalId) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen space-y-4">
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
					<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
						<div>
							<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
								チケット管理
							</h2>
							<p className="text-gray-600 dark:text-gray-400 mt-1">
								管理したいイベントを選択してください
							</p>
						</div>
						<button
							onClick={() => navigate("/organizer/events/create")}
							className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 font-medium"
						>
							イベントを作成
						</button>
					</div>

					<div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
						{eventFilterOptions.map((option) => (
							<button
								key={option.value}
								onClick={() => setEventFilter(option.value)}
								className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
									eventFilter === option.value
										? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-sm"
										: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
								}`}
							>
								{option.label}
							</button>
						))}
					</div>

					<div className="relative">
						{renderEventSelectionContent()}
						{isEventsLoading && (
							<div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80 dark:bg-gray-900/70 backdrop-blur-sm">
								<p className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
									<span
										className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"
										aria-hidden="true"
									/>
									読み込み中...
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	// ローディング中
	if (isLoading) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
					チケット管理
				</h2>
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
					<p className="text-gray-600 dark:text-gray-400">読み込み中...</p>
				</div>
			</div>
		);
	}

	// エラー発生時
	if (error || !event) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
					チケット管理
				</h2>
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
					<p className="text-red-600 dark:text-red-400">
						{error || "イベントが見つかりませんでした"}
					</p>
					<button
						onClick={() => navigate("/organizer/events")}
						className="mt-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 font-medium"
					>
						イベント一覧へ戻る
					</button>
				</div>
			</div>
		);
	}

	// チケットの合計数を計算
	const totalTickets = ticketTypes.reduce(
		(sum, type) => sum + (type.quantity || 0),
		0
	);

	// 販売済みチケットの合計数を計算
	const totalSoldTickets = ticketTypes.reduce(
		(sum, type) => sum + type.sold_tickets,
		0
	);

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen space-y-6">
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						チケット管理
					</h2>
					<div className="text-gray-600 dark:text-gray-400">
						{event.title} ({formatJapaneseDateTime(event.date, event.time)})
					</div>
				</div>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
				<div className="p-6 border-b border-gray-200 dark:border-gray-700">
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						チケット種別別の販売状況
					</h3>
				</div>

				{ticketTypes.length === 0 ? (
					<div className="p-8 text-center text-gray-600 dark:text-gray-400">
						チケット種別が登録されていません
					</div>
				) : (
					<>
						<div className="divide-y divide-gray-200 dark:divide-gray-700">
							{ticketTypes.map((type) => {
								const sellRatio =
									type.has_limit && type.quantity
										? Math.round((type.sold_tickets / type.quantity) * 100)
										: null;
								const revenue = type.is_free
									? 0
									: Math.round(type.price) * type.sold_tickets;

								return (
									<div
										key={type.id}
										className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
									>
										{/* スマホ表示用：チケット名と金額 */}
										<div className="flex items-start justify-between mb-3 md:hidden">
											<div className="flex-1">
												<h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
													{type.name}
												</h4>
												<p className="text-sm text-gray-600 dark:text-gray-400">
													{type.is_free
														? "無料"
														: `¥${Math.round(type.price).toLocaleString()}`}
												</p>
											</div>
										</div>
										{/* デスクトップ・スマホ共通のグリッド */}
										<div className="grid grid-cols-10 md:grid-cols-12 gap-4">
											{/* デスクトップ表示用：チケット名 */}
											<div className="hidden md:block md:col-span-3">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													チケット名
												</div>
												<h4 className="text-base font-semibold text-gray-900 dark:text-white">
													{type.name}
												</h4>
											</div>
											{/* デスクトップ表示用：金額 */}
											<div className="hidden md:block md:col-span-2">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													金額
												</div>
												<p className="text-base font-semibold text-gray-900 dark:text-white">
													{type.is_free
														? "無料"
														: `¥${Math.round(type.price).toLocaleString()}`}
												</p>
											</div>
											{/* 販売数 */}
											<div className="col-span-2 md:col-span-2">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													販売数
												</div>
												<div className="text-lg font-semibold text-gray-900 dark:text-white">
													{type.sold_tickets}
													<span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
														/ {type.has_limit ? type.quantity : "∞"}
													</span>
												</div>
											</div>
											{/* 販売率 */}
											<div className="col-span-5 md:col-span-3">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													販売率
												</div>
												<div className="text-lg font-semibold text-gray-900 dark:text-white">
													{sellRatio !== null ? (
														<>
															<span>{sellRatio}%</span>
															<div className="mt-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
																<div
																	className="bg-violet-600 dark:bg-violet-500 h-1.5 rounded-full transition-all"
																	style={{ width: `${sellRatio}%` }}
																></div>
															</div>
														</>
													) : (
														<span className="text-gray-400">-</span>
													)}
												</div>
											</div>
											{/* 売上 */}
											<div className="col-span-3 md:col-span-2">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													売上
												</div>
												<div className="text-lg font-semibold text-gray-900 dark:text-white">
													{type.is_free ? (
														<span className="text-gray-400">-</span>
													) : (
														`¥${revenue.toLocaleString()}`
													)}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>

						<div className="bg-gray-50 dark:bg-gray-900 p-6 border-t border-gray-200 dark:border-gray-700">
							<div className="flex items-center justify-between">
								<h4 className="text-base font-bold text-gray-900 dark:text-white">
									合計
								</h4>
								<div className="flex items-center gap-6">
									<div className="text-right">
										<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
											総販売数
										</div>
										<div className="text-lg font-bold text-gray-900 dark:text-white">
											{totalSoldTickets}
											{totalTickets > 0 && (
												<span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
													/ {totalTickets}
												</span>
											)}
										</div>
									</div>
									{totalTickets > 0 && (
										<div className="text-right">
											<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
												総販売率
											</div>
											<div className="text-lg font-bold text-gray-900 dark:text-white">
												{Math.round((totalSoldTickets / totalTickets) * 100)}%
											</div>
										</div>
									)}
									<div className="text-right">
										<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
											総売上
										</div>
										<div className="text-lg font-bold text-violet-600 dark:text-violet-400">
											¥
											{ticketTypes
												.reduce(
													(sum, type) =>
														sum +
														(type.is_free
															? 0
															: Math.round(type.price) * type.sold_tickets),
													0
												)
												.toLocaleString()}
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
				<div className="p-6 border-b border-gray-200 dark:border-gray-700">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
							購入者一覧・チェックイン管理
						</h3>
						<div className="flex items-center gap-3">
							<select
								value={selectedTicketType}
								onChange={(e) => setSelectedTicketType(e.target.value)}
								className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 font-medium"
							>
								<option value="all">すべてのチケット</option>
								{ticketTypes.map((type) => (
									<option key={type.id} value={type.id}>
										{type.name}
									</option>
								))}
							</select>
							<button
								onClick={handleExportCSV}
								className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm"
							>
								CSVでダウンロード
							</button>
						</div>
					</div>
				</div>

				<div className="divide-y divide-gray-200 dark:divide-gray-700">
					{(() => {
						const groupedPurchasers = getGroupedAndFilteredPurchasers();
						if (groupedPurchasers.length === 0) {
							return (
								<div className="p-8 text-center text-gray-600 dark:text-gray-400">
									{selectedTicketType !== "all"
										? "該当するチケットの購入データがありません"
										: "購入データがありません"}
								</div>
							);
						}

						return groupedPurchasers.map((group) => (
							<div
								key={group.email}
								className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
							>
								<div className="p-6">
									<div className="flex items-start justify-between mb-2">
										<div className="flex-1">
											<h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
												{group.user_name}
											</h4>
											<p className="text-sm text-gray-600 dark:text-gray-400">
												{group.email}
											</p>
										</div>
										<div className="flex items-center gap-4">
											<div className="text-right">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													総枚数
												</div>
												<div className="text-lg font-semibold text-gray-900 dark:text-white">
													{group.totalQuantity}枚
												</div>
											</div>
											<div className="text-right">
												<div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
													チェックイン済
												</div>
												<span
													className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
														group.totalCheckedIn === group.totalQuantity
															? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
															: group.totalCheckedIn > 0
															? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
															: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
													}`}
												>
													{group.totalCheckedIn}/{group.totalQuantity}
												</span>
											</div>
										</div>
									</div>

									<details className="group">
										<summary className="cursor-pointer text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium flex items-center gap-1">
											<span className="group-open:rotate-90 transition-transform inline-block">
												▶
											</span>
											チケット詳細を表示
										</summary>
										<div className="mt-4 space-y-4">
											{Array.from(group.ticketsByType.values()).map(
												(typeGroup, idx) => {
													const checkedCount = typeGroup.tickets.filter(
														(t) => t.is_redeemed
													).length;
													return (
														<div
															key={idx}
															className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden"
														>
															<div className="bg-gray-50 dark:bg-gray-700/50 px-3 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
																<span className="font-medium text-gray-900 dark:text-white">
																	{typeGroup.ticket_type}
																</span>
																<span className="text-sm text-gray-600 dark:text-gray-400">
																	{typeGroup.tickets.length}枚 (チェックイン済:{" "}
																	{checkedCount})
																</span>
															</div>
															<div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
																{typeGroup.tickets.map((ticket) => (
																	<div
																		key={ticket.id}
																		className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
																	>
																		<div className="flex-1">
																			<div className="text-sm font-medium text-gray-900 dark:text-white">
																				券番 #{ticket.sequence_number}
																			</div>
																			{ticket.redeemed_at && (
																				<div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
																					{ticket.redeemed_at}
																				</div>
																			)}
																		</div>
																		<button
																			onClick={() =>
																				handleToggleCheckIn(
																					ticket.id,
																					ticket.is_redeemed
																				)
																			}
																			className={`ml-3 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
																				ticket.is_redeemed
																					? "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-800"
																					: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
																			}`}
																		>
																			{ticket.is_redeemed
																				? "解除する"
																				: "入場処理"}
																		</button>
																	</div>
																))}
															</div>
														</div>
													);
												}
											)}
										</div>
									</details>
								</div>
							</div>
						));
					})()}
				</div>
			</div>
		</div>
	);
};

export default TicketManagement;
